import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { arrayMove } from '@dnd-kit/sortable';
import ProductCategoryService from 'services/product_category';
import { useApp } from 'contexts/app';

const service = new ProductCategoryService();

const sortByOrder = (list) =>
  [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

const useCategory = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({});
  const { modal, showLoading, prompt } = useApp();

  const onSearch = async (searchFilter) => {
    const list = await service.list({ info: searchFilter?.info });
    setData(sortByOrder(Array.isArray(list) ? list : []));
  };

  const delaySearch = useMemo(() => _.debounce(onSearch, 500, { leading: false }), []);

  useEffect(() => {
    delaySearch(filter);
    return () => delaySearch.cancel();
  }, [delaySearch, filter]);

  const setFilterValue = (name, value) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const loadData = async () => {
    const list = await service.list({ info: filter?.info });
    setData(sortByOrder(Array.isArray(list) ? list : []));
  };

  const createCategory = () => {
    prompt({
      title: 'Tạo danh mục',
      label: 'Nhập tên',
      onOk: async (text) => {
        showLoading(true);
        await service.create({ name: text });
        await loadData();
        showLoading(false);
      },
    });
  };

  const editCategory = (id) => {
    prompt({
      title: 'Chỉnh sửa danh mục',
      label: 'Nhập tên',
      onOk: async (text) => {
        showLoading(true);
        await service.update({ id, name: text });
        await loadData();
        showLoading(false);
      },
    });
  };

  const deleteCategory = (id) => {
    modal.confirm({
      title: 'Xoá?',
      content: 'Bạn có chắc chắn muốn xoá danh mục này?',
      onOk: async () => {
        showLoading(true);
        await service.delete(id);
        await loadData();
        showLoading(false);
      },
    });
  };

  const toggleVisible = async (record) => {
    showLoading(true);
    await service.update({
      id: record.id,
      name: record.name,
      sort_order: record.sort_order,
      is_visible: !record.is_visible,
    });
    await loadData();
    showLoading(false);
  };

  const reorderItems = async (activeId, overId) => {
    const activeIdx = data.findIndex((c) => String(c.id) === activeId);
    const overIdx = data.findIndex((c) => String(c.id) === overId);
    if (activeIdx === -1 || overIdx === -1 || activeIdx === overIdx) return;

    // Collect original sort_orders in current order
    const sortOrders = data.map((c, i) => c.sort_order ?? i + 1);

    // Compute new array order
    const newData = arrayMove([...data], activeIdx, overIdx);

    // Assign sort_orders from original positions to new positions
    const updatedData = newData.map((item, idx) => ({ ...item, sort_order: sortOrders[idx] }));

    // Optimistic update
    setData(updatedData);

    const updates = updatedData
      .filter((item) => {
        const original = data.find((d) => d.id === item.id);
        return original && original.sort_order !== item.sort_order;
      })
      .map((item) =>
        service.update({ id: item.id, name: item.name, sort_order: item.sort_order, is_visible: item.is_visible })
      );

    showLoading(true);
    await Promise.all(updates);
    await loadData();
    showLoading(false);
  };

  return {
    data,
    filter,
    setFilterValue,
    createCategory,
    editCategory,
    deleteCategory,
    toggleVisible,
    reorderItems,
  };
};

export default useCategory;
